import {
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { firstValueFrom, Subscription } from 'rxjs';
import { Apollo } from 'apollo-angular';
import {
  PropertyListQuery,
  ReportListQuery,
  ReportCategoryListQuery,
  ReportTemplateListQuery,
  ReportCategory,
  ReportTemplate,
} from 'gql/graphql';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AbbCommonModule } from 'app/abb-common.module';
import {
  deleteReportTemplateMutation,
  reportCategoryListQuery,
  reportTemplateListQuery,
} from '@graphql/report';
import { AbbTimeInputComponent } from '@components/abb-form/abb-time-picker';
import { propertyListQuery } from '@graphql/property';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DATE_LOCALE_GB_FORMAT } from '@utils/datetime';
import { REPORT_CATEGORY_DICTIONARY } from '../reports';
import { UserSettingsService } from '@services/user/user-settings.service';
import { OverlayService } from '@services/ui/overlay.service';
import { ReportTemplateSidePanelComponent } from '../report-template-side-panel/report-template-side-panel.component';
import { bindSignalToObservable } from '@utils/angular-ext';
import { omit } from '@utils/objects';
import { TranslateService } from '@ngx-translate/core';
import { noop } from '@utils/runtime';
import { LOADING } from '@utils/loader';
import { getErrorMessage } from '@utils/errors';
import { ToastService } from '@services/ui/toast.service';
import { t } from '@utils/strings';

@Component({
  selector: 'abb-report-form',
  standalone: true,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'reports',
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_LOCALE_GB_FORMAT },
  ],
  template: `
    <form
      id="report-form"
      class="w-full"
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
    >
      <div class="flex flex-col gap-3">
        <h3 class="text-base font-semibold text-abb-font/85">
          {{ 'Report Name' | t }}
        </h3>
        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Name' | t }}</mat-label>
          <input matInput formControlName="name" id="name" name="name" />
          @if(form.get('name')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>

        <h3 class="text-base font-semibold text-abb-font/85">
          {{ 'Template' | t }}
        </h3>
        <div class="form-field">
          <abb-mini-card-selection
            id="categoryId"
            name="categoryId"
            formControlName="categoryId"
            [options]="templates()"
            (addNewTemplate)="onOpenTemplatePanel('new')"
            (delete)="onDeleteTemplate($event)"
            (clone)="onOpenTemplatePanel('clone', $event)"
            (edit)="onOpenTemplatePanel('edit', $event)"
            (onCardSelected)="onTemplateSelected($event)"
          ></abb-mini-card-selection>
          <div class="form-field-wrapper form-field-bottom-align">
            <div class="form-field-error-wrapper">
              @if((formSubmitted() || form.controls.categoryId.touched) &&
              form.get('categoryId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </div>
          </div>
        </div>

        <h3
          [ngClass]="{
            'opacity-0': form.controls.categoryId.value?.includes('#')
          }"
          class="text-base font-semibold text-abb-font/85"
        >
          {{ 'Interval' | t }}
        </h3>
        @if(form.controls.interval; as intervalCtrl){
        <div
          [ngClass]="{
            'opacity-0': form.controls.categoryId.value?.includes('#')
          }"
          formGroupName="interval"
          class="flex flex-row flex-wrap w-full gap-3"
        >
          <mat-form-field color="primary">
            <mat-label>{{ 'From' | t }}</mat-label>
            <input
              matInput
              [matDatepicker]="fromPicker"
              [max]="maxDate"
              placeholder="{{ 'From' | t }}"
              formControlName="fromDate"
              id="fromDate"
            />
            <span
              class="min-w-[100px] w-fit max-w-full inline-flex flex-row justify-end flex-grow"
              matTextSuffix
            >
              @if(!fromPicker.opened) {
              <input
                formControlName="fromTime"
                type="time"
                id="fromTime"
                name="fromTime"
                class="bg-inherit focus:outline-none text-abb-font/85"
                step="1"
                [ngClass]="
                  intervalCtrl.controls.fromDate.value ||
                  intervalCtrl.get('fromDate')?.hasError('matDatepickerParse')
                    ? 'cursor-text'
                    : 'opacity-0 focus:opacity-100 pointer-events-none'
                "
                (click)="
                  intervalCtrl.controls.fromDate.value ||
                  intervalCtrl.get('fromDate')?.hasError('matDatepickerParse')
                    ? $event.stopPropagation()
                    : null
                "
              />}@else if(intervalCtrl.controls.fromDate.value) {
              {{ intervalCtrl.controls.fromTime.value }} }</span
            >
            <mat-datepicker-toggle
              id="fromdatepicker-toggle"
              matIconSuffix
              [for]="fromPicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #fromPicker [panelClass]="'panel'">
              <mat-datepicker-actions>
                <div class="!flex !flex-col !w-full">
                  @if(fromPicker.opened) {
                  <abb-time-input
                    class="!w-full !flex !justify-center"
                    formControlName="fromTime"
                  ></abb-time-input
                  >}
                  <div class="flex flex-row w-full gap-5 p-5 justify-end">
                    <abb-button
                      id="clear-btn"
                      [variant]="'clean'"
                      matDateRangePickerCancel
                      >{{ 'cancel' | t }}</abb-button
                    >
                    <abb-button id="apply-btn" matDateRangePickerApply>{{
                      'Apply' | t
                    }}</abb-button>
                  </div>
                </div>
              </mat-datepicker-actions>
            </mat-datepicker>
            @if(intervalCtrl.get('fromDate')?.hasError('matDatepickerParse')){
            <mat-error>{{ 'Invalid date' | t }}</mat-error>
            }
          </mat-form-field>

          <mat-form-field color="primary">
            <mat-label>{{ 'To' | t }}</mat-label>
            <input
              matInput
              [matDatepicker]="toPicker"
              [max]="maxDate"
              [min]="form.controls.interval.get('fromDate')?.value"
              placeholder="{{ 'To' | t }}"
              formControlName="toDate"
              input="toDate"
            />
            <span
              class="min-w-[100px] w-fit max-w-full inline-flex flex-row justify-end flex-grow"
              matTextSuffix
            >
              @if(!toPicker.opened) {
              <input
                formControlName="toTime"
                type="time"
                id="toTime"
                name="toTime"
                class="bg-inherit focus:outline-none text-abb-font/85"
                step="1"
                [ngClass]="
                  intervalCtrl.controls.toDate.value ||
                  intervalCtrl.get('toDate')?.hasError('matDatepickerParse')
                    ? 'cursor-text'
                    : 'opacity-0 focus:opacity-100 pointer-events-none'
                "
                (click)="
                  intervalCtrl.controls.toDate.value ||
                  intervalCtrl.get('toDate')?.hasError('matDatepickerParse')
                    ? $event.stopPropagation()
                    : null
                "
              />}@else if(intervalCtrl.controls.toDate.value) {
              {{ intervalCtrl.controls.toTime.value }} }</span
            >
            <mat-datepicker-toggle
              id="todatepicker-toggle"
              matSuffix
              [for]="toPicker"
            ></mat-datepicker-toggle>
            <mat-datepicker #toPicker [panelClass]="'panel'">
              <mat-datepicker-actions>
                <div class="!flex !flex-col !w-full">
                  @if(toPicker.opened) {
                  <abb-time-input
                    class="!w-full !flex !justify-center"
                    formControlName="toTime"
                  ></abb-time-input
                  >}
                  <div class="flex flex-row w-full gap-5 p-5 justify-end">
                    <abb-button
                      id="clean-btn"
                      [variant]="'clean'"
                      matDateRangePickerCancel
                      >{{ 'cancel' | t }}</abb-button
                    >
                    <abb-button id="apply-btn" matDateRangePickerApply>{{
                      'Apply' | t
                    }}</abb-button>
                  </div>
                </div>
              </mat-datepicker-actions>
            </mat-datepicker>
            @if(intervalCtrl.get('toDate')?.hasError('matDatepickerParse')){
            <mat-error>{{ 'Invalid date' | t }}</mat-error>
            } @if(timezone()){
            <mat-hint
              [align]="'end'"
              class="inline-flex items-center text-sm text-abb-font/65"
              >{{ '(' + timezone() + ')' }}</mat-hint
            >}
          </mat-form-field>
        </div>
        }
        <h3
          [ngClass]="{
            'opacity-0': form.controls.categoryId.value?.includes('#')
          }"
          class="text-base font-semibold text-abb-font/85"
        >
          {{ 'Properties' | t }}
        </h3>
        <mat-form-field
          [ngClass]="{
            'opacity-0': form.controls.categoryId.value?.includes('#')
          }"
          class="w-full"
          color="primary"
        >
          <mat-label>{{ 'Properties' | t }}</mat-label>
          <mat-select
            formControlName="properties"
            name="properties"
            id="properties"
            multiple
            (selectionChange)="propertySelected()"
            [panelClass]="'panel border-font-checkbox'"
          >
            <mat-select-trigger>
              <mat-chip-listbox>
                @for (propertyId of form.controls.properties.value; track
                propertyId) {
                <mat-chip
                  [id]="propertyId"
                  [removable]="true"
                  (removed)="onPropertyRemoved(propertyId)"
                  class="chips"
                >
                  {{ propertyWith(propertyId) }}
                  <abb-icon
                    [id]="'remove-' + propertyId"
                    matChipRemove
                    [name]="'mat/cancel'"
                    size="small"
                  ></abb-icon>
                </mat-chip>
                }
              </mat-chip-listbox>
            </mat-select-trigger>
            <input
              class="px-4 min-h-12 outline-none w-full placeholder:text-abb-font/40 bg-transparent"
              (keydown)="$event.stopPropagation()"
              [formControl]="searchCtrl"
              id="search-input"
              [placeholder]="'Search' | t"
            />
            @for (option of properties(); track option['value']) {
            <mat-option
              [id]="option['value']"
              [value]="option['value']"
              [ngClass]="{
                '!hidden': !filteredProperties().includes(option['value'])
              }"
              >{{ option['label'] }}</mat-option
            >
            }
          </mat-select>
          @if(form.get('properties')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>
      </div>
    </form>
  `,
  imports: [
    TPipe,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatDatepickerModule,
    AbbCommonModule,
    AbbTimeInputComponent,
  ],
})
export class ReportFormComponent implements OnDestroy {
  mode = input<'new' | 'edit'>();
  initValues = input<ReportListQuery['reportList'][number] | undefined>();
  formValidationStatusChange = output<boolean>();
  formSubmit = output<{
    form: ReportFormComponent['form'];
    isCustomTemplate: boolean;
  }>();
  formSubmitted = signal<boolean>(false);
  form = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
    categoryId: new FormControl<string | null>(null, [Validators.required]),
    interval: new FormGroup({
      fromDate: new FormControl<Date | null>(null, [Validators.required]),
      fromTime: new FormControl<string | null>(null, [Validators.required]),
      toDate: new FormControl<Date | null>(null, [Validators.required]),
      toTime: new FormControl<string | null>(null, [Validators.required]),
    }),
    properties: new FormControl<Array<string>>(new Array(), [
      Validators.required,
    ]),
  });
  searchCtrl = new FormControl<string>('');
  properties = signal<Array<Record<string, string>>>([]);
  filteredProperties = signal<Array<string>>([]);

  timezone = signal<string>('');
  maxDate = new Date();
  defaultTemplates = signal<Array<ReportCategory>>([]);
  customTemplates = signal<Array<ReportTemplate>>([]);
  templates = computed(() => [
    ...this.defaultTemplates()
      .filter((t) => REPORT_CATEGORY_DICTIONARY[t.type].defaultTemplate)
      .map((c) => ({
        id: c.id,
        icon: REPORT_CATEGORY_DICTIONARY[c.type].icon,
        title: REPORT_CATEGORY_DICTIONARY[c.type].title,
        description: REPORT_CATEGORY_DICTIONARY[c.type].description,
        editable: false,
      })),
    ...this.customTemplates().map((c) => ({
      id: c.id,
      icon: REPORT_CATEGORY_DICTIONARY[c.categoryType].icon,
      title: c.name,
      description: c.description,
      editable: true,
    })),
  ]);
  selectedTemplate = signal<any>(null);
  prefix = inject(TRANSLATION_PREFIX);
  private readonly subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly userService: UserSettingsService,
    private readonly overlayService: OverlayService,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    bindSignalToObservable(
      this.defaultTemplates,
      apollo.watchQuery<ReportCategoryListQuery>({
        query: reportCategoryListQuery,
      }).valueChanges,
      (x) => x.data.reportCategoryList
    );
    bindSignalToObservable(
      this.customTemplates,
      apollo.watchQuery<ReportTemplateListQuery>({
        query: reportTemplateListQuery,
      }).valueChanges,
      (x) => x.data.reportTemplateList
    );
    this.timezone.set(userService.timezone());
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {
        this.formValidationStatusChange.emit(!this.form.valid);
      }),
      this.searchCtrl.valueChanges.subscribe((searchQuery: string | null) => {
        this.filteredProperties.set(
          searchQuery
            ? this._filter(searchQuery)
            : this.properties()
                .map((p) => p['value'])
                .slice()
        );
      }),
      apollo
        .watchQuery<PropertyListQuery>({
          query: propertyListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            const propertyList = data.propertyList.map((p) => {
              return {
                value: p.id,
                label: p.name,
              };
            });
            this.properties.set(propertyList);
            this.filteredProperties.set(
              propertyList.map((p) => p['value']).slice()
            );
            console.log(this.templates(), 'templates after properties loaded');
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  propertyWith(propertyId: string) {
    return this.properties().find((p) => p['value'] === propertyId)!['label'];
  }

  propertySelected(): void {
    this.searchCtrl.setValue(null);
  }

  onPropertyRemoved(option: string) {
    const properties = this.form.controls.properties.value as Array<string>;
    this.removeFirst(properties, option);
    this.form.controls.properties.setValue(properties);
  }

  private removeFirst<T>(array: T[], toRemove: T): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  private _filter(searchQuery: string): Array<string> {
    return this.properties()
      .filter((p) =>
        p['label'].toLowerCase().trim().includes(searchQuery.toLowerCase())
      )
      .map((p) => p['value']);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onSubmit(): void {
    this.formSubmitted.set(true);
    this.formSubmit.emit({
      form: this.form,
      isCustomTemplate: this.customTemplates().some(
        (t) => t.id === this.form.value.categoryId
      ),
    });
  }

  onAddNewTemplate(): void {
    this.overlayService.openSidePanel(ReportTemplateSidePanelComponent, {
      mode: 'new',
      onSave: (template) => {
        this.form.controls.categoryId.setValue(template.id);
      },
    });
  }

  awaitingRes = inject(LOADING);
  onDeleteTemplate(id: string): void {
    const template = this.templates().find((t) => t.id === id)!;
    this.overlayService
      .openAlert({
        title: t(this.prefix, `Delete Report Template`, this.translate),
        message: t(
          this.prefix,
          `Are you sure you want to delete {name}`,
          this.translate,
          { name: template.title }
        ),
        confirmText: t(this.prefix, `Delete`, this.translate),
      })
      .then(() => {
        this.awaitingRes.set(true);
        firstValueFrom(
          this.apollo.mutate({
            mutation: deleteReportTemplateMutation,
            variables: {
              input: id,
            },
          })
        )
          .then(() =>
            this.apollo.client.refetchQueries({
              include: ['ReportTemplateList'],
            })
          )
          // TODO: is the default popup sufficient?
          .catch((err) => console.error(err))
          .finally(() => this.awaitingRes.set(false));
      }, noop);
  }

  onOpenTemplatePanel(mode: 'new'): void;
  onOpenTemplatePanel(mode: 'edit' | 'clone', id: string): void;
  onOpenTemplatePanel(mode: 'new' | 'edit' | 'clone', id?: string): void {
    this.overlayService.openSidePanel(
      ReportTemplateSidePanelComponent,
      mode === 'new'
        ? {
            mode,
            onSave: (template) => {
              this.form.controls.categoryId.setValue(template.id);
            },
          }
        : {
            mode,
            onSave: (template) => {
              this.form.controls.categoryId.setValue(template.id);
            },
            template: (() => {
              const custom = this.customTemplates().find((t) => t.id === id)!;
              return omit(
                {
                  ...custom,
                  widgets: custom.widgets.map((c) =>
                    omit(c, ['__typename', 'id'])
                  ),
                },
                ['__typename']
              );
            })(),
          }
    );
  }

  onTemplateSelected(event: any): void {
    if (event?.editable) {
      this.form.controls.properties.clearValidators();
      this.form.controls.properties.updateValueAndValidity();
      this.form.controls.interval.clearValidators();
      this.form.controls.interval.updateValueAndValidity();

      // Rimuovi i validators da ciascun campo in `interval`
      Object.values(
        (this.form.controls.interval as FormGroup).controls
      ).forEach((control) => {
        control.clearValidators();
        control.updateValueAndValidity();
      });
    } else {
      this.form.controls.properties.setValidators([Validators.required]);
      this.form.controls.properties.updateValueAndValidity();
      this.form.controls.interval.setValidators([Validators.required]);
      this.form.controls.interval.updateValueAndValidity();
      Object.values(
        (this.form.controls.interval as FormGroup).controls
      ).forEach((control) => {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity();
      });
    }
  }
}
